import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { flushSync } from "react-dom";
import api, { refreshSession } from "@/lib/api";
import {
  getToken,
  clearAuthSession,
  initAuthStorage,
  bumpAuthStorageGeneration,
  getAuthStorageGeneration,
  hasStoredSession,
  persistAuthSession,
  getRememberMe,
  isLoggedOut,
  markLoggedOut,
  prepareAuthForLogin,
  clearLoggedOutFlag,
  migrateCurrentSessionToAccounts,
  preserveActiveAccountForMultiLogin,
} from "@/lib/authStorage";
import {
  getSavedAccounts,
  getSavedAccountEntry,
  removeSavedAccount,
  touchSavedAccount,
  upsertSavedAccount,
  getSavedAccountsWithTokens,
  updateSavedAccountToken,
  clearAllSavedAccounts,
} from "@/lib/authAccounts";
import { dashboardPathForUser } from "@/lib/authRedirect";
import {
  fetchLinkedAccounts,
  linkAccounts,
  mergeLinkedAndLocalAccounts,
} from "@/lib/linkedAccounts";

export { dashboardPathForUser };

const AuthContext = createContext(null);

function isAuthError(err) {
  const status = err?.response?.status;
  return status === 401 || status === 403;
}

function isNetworkError(err) {
  return !err?.response;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(() => hasStoredSession());
  const [savedAccounts, setSavedAccounts] = useState(() => getSavedAccounts());
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const validatePromiseRef = useRef(null);
  const authBusyRef = useRef(false);
  const userRef = useRef(null);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const refreshSavedAccounts = useCallback(() => {
    setSavedAccounts(getSavedAccounts());
  }, []);

  const refreshLinkedAccounts = useCallback(async () => {
    try {
      const accounts = await fetchLinkedAccounts();
      setLinkedAccounts(accounts);
      return accounts;
    } catch {
      setLinkedAccounts([]);
      return [];
    }
  }, []);

  const applyAuth = useCallback(async (data, rememberMe = getRememberMe()) => {
    await persistAuthSession(data, rememberMe);
    if (data?.token && data?.user_id) {
      updateSavedAccountToken(data.user_id, data.token);
    }
    flushSync(() => {
      setUser(data);
      setLoading(false);
      refreshSavedAccounts();
    });
    return data;
  }, [refreshSavedAccounts]);

  const invalidateSession = useCallback(async ({ keepSavedAccounts = false } = {}) => {
    if (authBusyRef.current) return;

    if (!keepSavedAccounts) {
      const currentUser = userRef.current;
      if (currentUser?.user_id) {
        removeSavedAccount(currentUser.user_id);
        refreshSavedAccounts();
      }
    }
    markLoggedOut();
    setUser(null);
    bumpAuthStorageGeneration();
    await clearAuthSession();
  }, [refreshSavedAccounts]);

  const fetchAuthenticatedUser = useCallback(async () => {
    try {
      await refreshSession();
    } catch {
      /* /auth/me validera ensuite */
    }
    const { data } = await api.get("/auth/me", { timeout: 30000 });
    return data;
  }, []);

  const validateSession = useCallback(async () => {
    if (authBusyRef.current) return hasStoredSession();
    if (validatePromiseRef.current) return validatePromiseRef.current;

    const run = async () => {
      if (isLoggedOut()) {
        setUser(null);
        return false;
      }

      const genAtStart = getAuthStorageGeneration();
      const sessionWasReplaced = () => genAtStart !== getAuthStorageGeneration();
      const onLoginPage = () =>
        typeof window !== "undefined" && window.location.pathname === "/login";

      try {
        await initAuthStorage();
        if (sessionWasReplaced() || authBusyRef.current) return hasStoredSession();
        if (isLoggedOut()) {
          setUser(null);
          return false;
        }

        if (!getToken()) {
          setUser(null);
          return false;
        }

        if (sessionWasReplaced() || authBusyRef.current) return hasStoredSession();

        try {
          let data;
          try {
            data = await fetchAuthenticatedUser();
          } catch (err) {
            if (!isAuthError(err) || sessionWasReplaced() || authBusyRef.current) {
              throw err;
            }
            await refreshSession();
            data = await fetchAuthenticatedUser();
          }

          if (sessionWasReplaced() || authBusyRef.current) return hasStoredSession();
          await applyAuth(data);
          setUser(data);
          migrateCurrentSessionToAccounts(getToken(), data);
          refreshSavedAccounts();
          await refreshLinkedAccounts();
          return true;
        } catch (err) {
          if (sessionWasReplaced() || authBusyRef.current) return false;
          if (isAuthError(err)) {
            await invalidateSession({ keepSavedAccounts: onLoginPage() });
            return false;
          }
          if (isNetworkError(err)) {
            return false;
          }
          await invalidateSession({ keepSavedAccounts: onLoginPage() });
          return false;
        }
      } finally {
        validatePromiseRef.current = null;
      }
    };

    validatePromiseRef.current = run();
    return validatePromiseRef.current;
  }, [applyAuth, fetchAuthenticatedUser, invalidateSession, refreshSavedAccounts, refreshLinkedAccounts]);

  const checkAuth = useCallback(async () => {
    if (authBusyRef.current) return;

    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }

    if (isLoggedOut()) {
      setUser(null);
      setLoading(false);
      return;
    }

    if (hasStoredSession()) {
      setLoading(true);
      await validateSession();
    } else {
      setUser(null);
    }

    setLoading(false);
  }, [validateSession]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;

    const onSwUpdate = async () => {
      if (isLoggedOut() || !hasStoredSession()) return;
      await validateSession();
    };

    navigator.serviceWorker.addEventListener("controllerchange", onSwUpdate);
    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", onSwUpdate);
    };
  }, [validateSession]);

  useEffect(() => {
    const onResume = async () => {
      if (document.visibilityState !== "visible" || isLoggedOut() || !hasStoredSession()) return;
      await validateSession();
    };

    const onPageShow = async (event) => {
      if (isLoggedOut() || !hasStoredSession()) return;
      if (event.persisted) {
        await validateSession();
      }
    };

    document.addEventListener("visibilitychange", onResume);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onResume);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [validateSession]);

  const login = async (
    email,
    password,
    rememberMe = true,
    { addAccount = false, linkVerificationId = null } = {}
  ) => {
    const previousUserId = addAccount ? userRef.current?.user_id : null;
    authBusyRef.current = true;
    try {
      if (addAccount) {
        preserveActiveAccountForMultiLogin();
      }
      await prepareAuthForLogin();
      clearLoggedOutFlag();
      const { data } = await api.post("/auth/login", {
        email,
        password,
        remember_me: rememberMe,
        add_account: addAccount,
      });
      const loggedIn = await applyAuth(data, rememberMe);
      if (addAccount && previousUserId && loggedIn.user_id !== previousUserId) {
        try {
          await linkAccounts(previousUserId, linkVerificationId);
          await refreshLinkedAccounts();
        } catch {
          /* liaison serveur non bloquante — compte actif quand même */
        }
      }
      return loggedIn;
    } finally {
      authBusyRef.current = false;
    }
  };

  const register = async (formData, { addAccount = false } = {}) => {
    authBusyRef.current = true;
    try {
      if (addAccount) {
        preserveActiveAccountForMultiLogin();
      }
      await prepareAuthForLogin();
      clearLoggedOutFlag();
      const { data } = await api.post("/auth/register", {
        ...formData,
        remember_me: true,
        add_account: addAccount,
      });
      return await applyAuth(data, true);
    } finally {
      authBusyRef.current = false;
    }
  };

  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeSession = async (sessionId) => {
    authBusyRef.current = true;
    try {
      await prepareAuthForLogin();
      clearLoggedOutFlag();
      const { data } = await api.post("/auth/session", { session_id: sessionId });
      return await applyAuth(data, true);
    } finally {
      authBusyRef.current = false;
    }
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      return applyAuth(data);
    } catch (err) {
      if (isAuthError(err)) {
        await invalidateSession();
      }
      return null;
    }
  };

  const fetchMeWithRetry = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me", { timeout: 30000 });
      return data;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const isServerGlitch =
        (err?.response?.status ?? 0) >= 500
        || (typeof detail === "string" && /more than one result/i.test(detail));

      if (isServerGlitch) {
        await refreshSession();
        const { data } = await api.get("/auth/me", { timeout: 30000 });
        return data;
      }
      throw err;
    }
  }, []);

  const switchAccount = useCallback(async (userId) => {
    if (user?.user_id === userId) return user;

    const entry = getSavedAccountEntry(userId);
    if (!entry?.token) {
      throw new Error("Compte introuvable sur cet appareil");
    }

    authBusyRef.current = true;
    try {
      const activeToken = getToken();
      if (user?.user_id && activeToken) {
        upsertSavedAccount(activeToken, user);
      }

      clearLoggedOutFlag();
      bumpAuthStorageGeneration();
      await prepareAuthForLogin();

      const sessionData = {
        token: entry.token,
        user_id: entry.user_id,
        email: entry.email,
        name: entry.name,
        role: entry.role,
        picture: entry.picture,
      };

      await persistAuthSession(sessionData, true);
      touchSavedAccount(userId);
      refreshSavedAccounts();

      const data = await fetchMeWithRetry();
      if (data?.token) {
        updateSavedAccountToken(data.user_id, data.token);
      }
      await applyAuth(data);
      refreshLinkedAccounts().catch(() => {});
      return data;
    } catch (err) {
      if (isAuthError(err)) {
        removeSavedAccount(userId);
        refreshSavedAccounts();
      }
      throw err;
    } finally {
      authBusyRef.current = false;
    }
  }, [user, applyAuth, refreshSavedAccounts, fetchMeWithRetry, refreshLinkedAccounts]);

  const removeAccountFromDevice = useCallback((userId) => {
    if (!userId) return;
    if (userId === user?.user_id) {
      throw new Error("Déconnectez-vous pour retirer le compte actif");
    }
    removeSavedAccount(userId);
    refreshSavedAccounts();
  }, [user, refreshSavedAccounts]);

  const logout = useCallback(async ({ all = false } = {}) => {
    const currentId = user?.user_id;

    try {
      await api.post("/auth/logout");
    } catch {
      /* purge locale même si le serveur ne répond pas */
    }

    if (all) {
      clearAllSavedAccounts();
      markLoggedOut();
      setUser(null);
      setLinkedAccounts([]);
      bumpAuthStorageGeneration();
      await clearAuthSession();
      refreshSavedAccounts();
      return { switchedTo: null, all: true };
    }

    if (currentId) {
      removeSavedAccount(currentId);
      refreshSavedAccounts();
    }

    const remaining = getSavedAccountsWithTokens().filter((a) => a.user_id !== currentId);
    if (remaining.length > 0) {
      clearLoggedOutFlag();
      const next = remaining[0];
      bumpAuthStorageGeneration();
      await prepareAuthForLogin();
      const sessionData = {
        token: next.token,
        user_id: next.user_id,
        email: next.email,
        name: next.name,
        role: next.role,
        picture: next.picture,
      };
      await persistAuthSession(sessionData, true);
      touchSavedAccount(next.user_id);
      refreshSavedAccounts();
      try {
        const { data } = await api.get("/auth/me", { timeout: 30000 });
        await applyAuth(data);
        return { switchedTo: data, all: false };
      } catch {
        removeSavedAccount(next.user_id);
        refreshSavedAccounts();
      }
    }

    markLoggedOut();
    setUser(null);
    setLinkedAccounts([]);
    bumpAuthStorageGeneration();
    await clearAuthSession();
    refreshSavedAccounts();
    return { switchedTo: null, all: false };
  }, [user, applyAuth, refreshSavedAccounts]);

  const displayAccounts = mergeLinkedAndLocalAccounts(linkedAccounts, savedAccounts, user);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      savedAccounts,
      linkedAccounts,
      displayAccounts,
      refreshLinkedAccounts,
      login,
      register,
      loginWithGoogle,
      exchangeSession,
      logout,
      removeAccountFromDevice,
      switchAccount,
      refreshUser,
      refreshSavedAccounts,
      setUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);